import { MotionConfig, fadeUp, motion, stagger } from '@/lib/motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useBrand } from '@/brand/brand-provider';
import { Button } from '@/components/ui/button';
import { JsonLd, Seo } from '@/lib/seo';

export interface IndustryFaq {
  q: string;
  a: string;
}

export interface IndustryFeature {
  icon: LucideIcon;
  title: string;
  desc: string;
}

export interface IndustryPageConfig {
  /** SEO */
  seoTitle: string;
  seoDescription: string;
  path: string;
  /** Hero */
  badge: string;
  headline: string;
  subheadline: string;
  /** Pain points (3) */
  pains: string[];
  /** Feature cards */
  features: IndustryFeature[];
  /** Testimonial / social proof */
  quote?: { text: string; author: string; role: string };
  /** FAQ */
  faqs: IndustryFaq[];
  /** CTA */
  ctaHeadline: string;
  ctaSub: string;
}

export function IndustryPage({ config }: { config: IndustryPageConfig }): JSX.Element {
  const brand = useBrand();
  return (
    <MotionConfig reducedMotion="user">
      <Seo
        title={config.seoTitle}
        description={config.seoDescription}
        path={config.path}
      />
      <JsonLd
        schema={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: config.faqs.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a },
          })),
        }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-0 h-[500px] w-[900px] -translate-x-1/2 -translate-y-1/3 rounded-full bg-gradient-to-br from-[var(--brand-accent)]/20 via-[var(--brand-accent)]/5 to-transparent blur-3xl" />
          <div className="animate-orb-drift-a absolute right-0 top-32 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
        </div>
        <div className="container py-14 sm:py-20">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="mx-auto max-w-3xl text-center"
          >
            <motion.span
              variants={fadeUp}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-accent)]/10 px-3 py-1 text-xs font-semibold text-[var(--brand-accent)]"
            >
              {config.badge}
            </motion.span>
            <motion.h1
              variants={fadeUp}
              className="mt-5 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
            >
              {config.headline}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="mt-5 text-lg text-muted-foreground"
            >
              {config.subheadline}
            </motion.p>
            <motion.div
              variants={fadeUp}
              className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center"
            >
              <Button asChild size="lg">
                <Link to="/signup">
                  Get started <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/pricing">See pricing</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pain points */}
      <section className="border-y border-border bg-muted/20 py-16">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Sound familiar?
            </h2>
          </div>
          <motion.ul
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={stagger}
            className="mx-auto mt-10 grid max-w-3xl gap-4 sm:grid-cols-3"
          >
            {config.pains.map((pain) => (
              <motion.li
                key={pain}
                variants={fadeUp}
                className="flex items-start gap-3 rounded-xl border border-border bg-background p-5 text-sm text-muted-foreground"
              >
                <span className="mt-0.5 shrink-0 text-destructive">✕</span>
                {pain}
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* Features */}
      <section className="py-14 sm:py-20">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              {brand.displayName} handles it all.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Trained on your services, hours, and policies. Answering 24/7.
            </p>
          </div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={stagger}
            className="mx-auto mt-14 grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {config.features.map(({ icon: Icon, title, desc }) => (
              <motion.div
                key={title}
                variants={fadeUp}
                className="rounded-2xl border border-border bg-background p-6"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--brand-accent)]/10">
                  <Icon className="h-5 w-5 text-[var(--brand-accent)]" />
                </div>
                <h3 className="mt-4 font-semibold">{title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Quote */}
      {config.quote && (
        <section className="border-y border-border bg-muted/20 py-16">
          <div className="container">
            <motion.blockquote
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mx-auto max-w-2xl text-center"
            >
              <p className="text-xl font-medium italic text-foreground">
                &ldquo;{config.quote.text}&rdquo;
              </p>
              <footer className="mt-4 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">
                  {config.quote.author}
                </span>
                {' '}— {config.quote.role}
              </footer>
            </motion.blockquote>
          </div>
        </section>
      )}

      {/* What's included */}
      <section className="py-14 sm:py-16">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-center text-3xl font-bold tracking-tight sm:text-4xl">
              Everything included, zero setup headaches.
            </h2>
            <motion.ul
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="mt-10 grid gap-3 sm:grid-cols-2"
            >
              {[
                '24/7 call answering — never miss a lead',
                'Natural AI voice, sub-second response',
                'Captures names, numbers & booking requests',
                'Warm transfer to your team when needed',
                'Same knowledge base as your website chat',
                'Dashboard with call transcripts & summaries',
                'Custom greetings matching your brand',
                'Live in under 10 minutes',
              ].map((item) => (
                <motion.li
                  key={item}
                  variants={fadeUp}
                  className="flex items-center gap-2 text-sm"
                >
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  {item}
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border py-14 sm:py-16">
        <div className="container">
          <div className="mx-auto max-w-2xl">
            <h2 className="text-center text-3xl font-bold tracking-tight">
              Frequently asked questions
            </h2>
            <dl className="mt-10 space-y-6">
              {config.faqs.map(({ q, a }) => (
                <div key={q} className="border-b border-border pb-6">
                  <dt className="font-semibold">{q}</dt>
                  <dd className="mt-2 text-sm text-muted-foreground">{a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-16">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-2xl rounded-3xl bg-gradient-to-br from-[var(--brand-accent)]/20 via-[var(--brand-accent)]/10 to-transparent border border-[var(--brand-accent)]/20 px-8 py-14 text-center"
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              {config.ctaHeadline}
            </h2>
            <p className="mt-4 text-muted-foreground">{config.ctaSub}</p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button asChild size="lg">
                <Link to="/signup">
                  Get started <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/contact">Talk to us</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </MotionConfig>
  );
}
